import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { historicalCustomerCandidates, historicalOriginalCustomer } from '../lib/legacyHistoricJobImport'

const shown = value => String(value || '').trim() || 'Not supplied by old system'
const customerName = customer => [customer.first_name, customer.last_name].filter(Boolean).join(' ')

export default function LegacyHistoricJobModal({ record, customers, owner, onClose, onLinked }) {
  const facts = record.facts || {}
  const match = historicalCustomerCandidates(facts, customers)
  const linkedCustomer = customers.find(customer => customer.id === record.customer_id)
  const [customerId, setCustomerId] = useState(record.customer_id || '')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { setCustomerId(record.customer_id || ''); setNote(''); setError('') },
    [record.id, record.customer_id])

  async function saveLink() {
    if (!owner || busy || customerId === (record.customer_id || '')) return
    setBusy(true)
    setError('')
    try {
      const { error: rpcError } = await supabase.rpc('link_legacy_historic_job_customer', {
        p_record_id: record.id, p_customer_id: customerId || null, p_note: note.trim(),
      })
      if (rpcError) throw rpcError
      onLinked({ ...record, customer_id: customerId || null, linked_at: new Date().toISOString(),
        link_note: note.trim() })
    } catch (err) {
      setError(err.message || 'Customer link was not saved.')
    } finally {
      setBusy(false)
    }
  }

  const detail = (label, value) => <div className="detail-row" key={label}>
    <span className="detail-label">{label}</span><span className="detail-val">{shown(value)}</span>
  </div>

  return <div className="modal-overlay" role="presentation">
    <div className="modal modal-wide legacy-historic-modal" role="dialog" aria-modal="true"
      aria-label={`Historical job ${record.source_reference}`}>
      <div className="modal-header">
        <div><div className="refresh-kicker">Legacy / previous operating system</div>
          <div className="modal-title">Historical Job {record.source_reference}</div>
          <div className="text-muted fs12">Read-only reference. Not an active Rebound job.</div></div>
        <button type="button" className="close-btn" onClick={onClose} aria-label="Close historical job">X</button>
      </div>
      <div className="sec">Original Job</div>
      {detail('Source Job ID', record.source_reference)}
      {detail('Original Customer', historicalOriginalCustomer(facts))}
      {detail('Bike', facts.bikes)}
      {detail('Created', facts.created)}
      {detail('Scheduled', facts.scheduled)}
      {detail('Due Date', facts.due_date)}
      {detail('Collection / Drop-off', facts.collection_drop_off)}
      {detail('Assigned Mechanic', facts.assigned_mechanic)}
      {detail('Accepted Status', facts.accepted_status)}
      {detail('Old-System Status', facts.status)}
      {detail('Booking Type', facts.booking_type)}
      {detail('Billing Type', facts.billing_type)}
      {detail('Warranty', facts.is_warranty_job)}
      {detail('Motability', facts.is_motability_job)}
      {detail('Leasing', facts.is_leasing_job)}
      {detail('Invoice Status', facts.invoice_status)}
      {detail('Booked In', facts.status_booked_in)}
      {detail('Bike Arrived', facts.status_bike_arrived)}
      {detail('Waiting for Parts', facts.status_waiting_for_parts)}
      {detail('Waiting for Customer Approval', facts.status_waiting_for_customer_approval)}
      {detail('Working On', facts.status_working_on)}
      {detail('Bike Ready', facts.status_bike_ready)}
      {detail('Completed', facts.status_completed)}
      <div className="sec">Historical Invoice Facts</div>
      {detail('Old-System Invoice Number', facts.invoice_number)}
      {detail('Invoice Created', facts.invoice_created)}
      {detail('Invoice Items', facts.invoice_items)}
      {detail('Historical Amount', facts.historical_amount
        ? [facts.currency, facts.historical_amount].filter(Boolean).join(' ') : '')}
      {detail('Service Revenue', facts.service_revenue)}
      {detail('Parts Revenue', facts.parts_revenue)}
      {detail('Free Text Items Revenue', facts.free_text_items_revenue)}
      {detail('Collection Fees', facts.collection_fees)}
      {detail('Discounts', facts.discounts)}
      {detail('Total inc Tax', facts.total_inc_tax)}
      {detail('Total exc Tax', facts.total_exc_tax)}
      {detail('Effective Tax %', facts.effective_tax_percent)}
      {detail('Tax Amount', facts.tax_amount)}
      {detail('Paid (old system)', facts.paid)}
      {detail('Outstanding (old system)', facts.outstanding)}
      {detail('Payment Taken On', facts.payment_taken_on)}
      {detail('Payment Transaction (old system)', facts.payment_transaction)}
      <div className="field-hint">These are old-system facts, not a Rebound invoice or payable amount.</div>
      <div className="sec">Provenance</div>
      {detail('Source', record.source_system || 'Previous operating system')}
      {detail('Imported', record.created_at ? new Date(record.created_at).toLocaleString() : '')}
      {detail('Import Batch', record.batch_id)}
      {detail('Linked Customer', linkedCustomer ? customerName(linkedCustomer) : 'Unlinked')}
      {owner && <div className="legacy-historic-link">
        <div className="sec">Owner-Reviewed Customer Link</div>
        <div className="field-hint">{match.kind === 'contact' ? 'One matching contact; confirm the identity.'
          : match.kind === 'name_only' ? 'Name-only candidates are suggestions, never automatic links.'
          : match.kind === 'ambiguous' ? 'Contact or name evidence conflicts; review carefully.'
          : 'No matching customer found. Leave this reference unlinked until reviewed.'}</div>
        {match.candidates.length > 0 && <div className="text-muted fs12">Suggested: {match.candidates.map(customerName).join(', ')}</div>}
        <div className="field"><label>Customer</label><select value={customerId}
          onChange={event => { setCustomerId(event.target.value); setError('') }}>
          <option value="">Unlinked</option>
          {customers.map(customer => <option key={customer.id} value={customer.id}>
            {customerName(customer)}{customer.email ? ` - ${customer.email}` : ''}
          </option>)}
        </select></div>
        <div className="field"><label>Review Note</label><input value={note}
          onChange={event => setNote(event.target.value)} placeholder="Why is this the correct customer?" /></div>
        {error && <div className="alert alert-error">{error}</div>}
        <button type="button" className="btn btn-primary" onClick={saveLink}
          disabled={busy || customerId === (record.customer_id || '') || note.trim().length < 5}>
          {busy ? 'Saving...' : 'Confirm Customer Link'}
        </button>
      </div>}
    </div>
  </div>
}
